import { MPFLink } from 'volto-ministerio-publico-federal/components/UI';

interface BreadcrumbItem {
  url: string;
  title: string;
}

interface BreadcrumbsProps {
  root?: string;
  items: BreadcrumbItem[];
}

/** Rótulo do primeiro degrau do rastro, sempre um link para a raiz do site. */
const HOME_TITLE = 'Início';

/**
 * Quantos degraus o rastro mostra, no máximo (Design System, seção do
 * breadcrumb; AB#11381). Acima disso os níveis do meio viram reticências:
 * ficam o "Início", as reticências e os três últimos.
 */
export const MAX_NIVEIS = 5;

/** Marcador dos níveis omitidos; não é um item do backend. */
const RETICENCIAS = { url: '', title: '…' };

/**
 * Prefixa o rastro com o item "Início", apontando para a raiz do site — ou
 * para a raiz multilíngue, quando o expander de breadcrumbs devolve `root`
 * (site com idiomas usa uma pasta de idioma como raiz, não `/`).
 *
 * Sem itens, ou com um único item que já é a própria raiz, o rastro sai como
 * veio: prefixar aqui duplicaria o "Início" ou o deixaria sozinho apontando
 * para a página atual.
 */
const withHome = (
  root: string | undefined,
  items: BreadcrumbItem[],
): BreadcrumbItem[] => {
  const homeUrl = root || '/';
  const alreadyAtHome =
    items.length === 0 || (items.length === 1 && items[0].url === homeUrl);

  return alreadyAtHome
    ? items
    : [{ title: HOME_TITLE, url: homeUrl }, ...items];
};

/**
 * Limita o rastro a `MAX_NIVEIS` degraus: com mais que isso, o primeiro
 * (Início) fica, os últimos três ficam, e o que há entre eles vira um único
 * marcador de reticências.
 */
export const limitarNiveis = (items: BreadcrumbItem[]): BreadcrumbItem[] => {
  if (items.length <= MAX_NIVEIS) return items;
  const ultimos = MAX_NIVEIS - 2;
  return [items[0], RETICENCIAS, ...items.slice(-ultimos)];
};

/** Separador entre degraus: CaretRight de 16 px, só visual. */
const Separador = () => (
  <svg
    className="mpf-breadcrumb__separador"
    viewBox="0 0 16 16"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M6 3l5 5-5 5" />
  </svg>
);

export const HeaderBreadcrumbs = ({ props }: { props: BreadcrumbsProps }) => {
  const items = limitarNiveis(withHome(props.root, props.items));
  const ultimo = items.length - 1;

  return (
    <div className="mpf-breadcrumb">
      <div className="mpf-breadcrumb__container">
        <div className="mpf-breadcrumb__content">
          <nav aria-label="breadcrumb" className="mpf-breadcrumb__nav">
            <ol className="mpf-breadcrumb__itens">
              {items.map((item, index) => {
                if (index === ultimo) {
                  return (
                    <li
                      key={item.url}
                      className="mpf-breadcrumb__item mpf-breadcrumb__itens--active"
                      aria-current="page"
                      title={item.title}
                    >
                      {item.title}
                    </li>
                  );
                }
                if (item === RETICENCIAS) {
                  return (
                    <li
                      key="reticencias"
                      className="mpf-breadcrumb__item mpf-breadcrumb__item--reticencias"
                    >
                      <span className="sr-only">
                        Níveis intermediários omitidos
                      </span>
                      <span aria-hidden="true">…</span>
                      <Separador />
                    </li>
                  );
                }
                return (
                  <li className="mpf-breadcrumb__item" key={item.url}>
                    <MPFLink href={item.url} className="mpf-breadcrumb__link">
                      {item.title}
                    </MPFLink>
                    <Separador />
                  </li>
                );
              })}
            </ol>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default HeaderBreadcrumbs;
